import { ConnectionStateEnum, DeviceModel, InstallationStateEnum } from '@core/models';

// D473: the backend turns the new state fields on per partner. Until the contract step removes
// the old fields, this is the only place that reads them; it mirrors the device-func mapping
// (helper/classes/device_status.py, D447).
const LEGACY_INSTALLED_STAGE = 'registered';
const LEGACY_FAILED_STAGES = new Set([
  'verification failed',
  'provisioning failed',
  'connection failed',
  'connection timed-out',
  'configuration failed',
]);
const LEGACY_IN_PROGRESS_STAGES = new Set([
  'linked',
  'verified',
  'provisioning',
  'provisioned',
  'connection initialized',
  'configuration pending',
  'configured',
  'reprovision device',
]);
const LEGACY_ONLINE_STATUS = 'device online';

export interface DeviceStates {
  /** Null when the old provisioning stage is not one this portal knows (AC1). */
  installationState: InstallationStateEnum | null;
  connectionState: ConnectionStateEnum | null;
}

type DeviceStateSource = Partial<
  Pick<
    DeviceModel,
    'installationState' | 'connectionState' | 'deviceInstallationStatus' | 'deviceOnlineStatus'
  >
>;

/** The device's installation and connection state: the API fields when present, otherwise
 * derived from the old provisioning stage and online status. */
export function resolveDeviceStates(device: DeviceStateSource): DeviceStates {
  if (device.installationState) {
    return {
      installationState: device.installationState,
      connectionState: device.connectionState ?? null,
    };
  }
  const stage = device.deviceInstallationStatus?.toLowerCase();
  let installationState: InstallationStateEnum | null = InstallationStateEnum.ToBeInstalled;
  if (stage === LEGACY_INSTALLED_STAGE) {
    installationState = InstallationStateEnum.Installed;
  } else if (stage && LEGACY_FAILED_STAGES.has(stage)) {
    installationState = InstallationStateEnum.InstallationFailed;
  } else if (stage && !LEGACY_IN_PROGRESS_STAGES.has(stage)) {
    // An unknown stage is never shown as To be installed: no state, and a loud error.
    console.error(`Unknown device installation stage: ${device.deviceInstallationStatus}`);
    installationState = null;
  }
  const connectionState =
    device.deviceOnlineStatus?.toLowerCase() === LEGACY_ONLINE_STATUS
      ? ConnectionStateEnum.Online
      : ConnectionStateEnum.Offline;
  return { installationState, connectionState };
}
